import { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DefaultStatisticsCard from "./components/RecvCard";
import {
  miscGRNColumns,
  miscPutawayGeneratedColumns,
  miscCompletedColumns,
  miscCancelledColumns,
  miscOpenColumns,
} from "./components/columns";
import { useQuery } from "@tanstack/react-query";
import { ListLayout } from "ui-components/Layout";
import { TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";
import { CSVLink } from "react-csv";
import { useCustomerValue } from "providers/CustomerContext";
import { getRecvDashboardStatusCount, getRecvDashboard } from "services/data.dashboard.service";
import { LoadingDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { ExportDialog } from "ui-components/Alert";

function MiscDashboard() {
  //filter by customer code
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode);
  const [dashboardType, setDashboardType] = useState("Open");
  const [columns, setColumns] = useState(miscGRNColumns);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();

  let currentDate = new Date();
  const [dateEnd, setDateEnd] = useState(new Date(currentDate));
  const [dateStart, setDateStart] = useState(
    new Date(currentDate.setDate(currentDate.getDate() - 7))
  );
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "GRN", count: 0 },
    { label: "PutawayGenerated", count: 0 },
    { label: "Completed", count: 0 },
    { label: "Cancelled", count: 0 },
  ]);

  const { isFetching, refetch } = useQuery({
    queryKey: ["misc-dashboard", { dashboardType, customerCode }],
    enabled: enabled,
    queryFn: () =>
      getRecvDashboard({
        orderType: "Misc",
        dashboardType: dashboardType,
        dateStart: Boolean(dateStart) ? dayjs(dateStart).format("YYYY-MM-DD") : null,
        dateEnd: Boolean(dateEnd) ? dayjs(dateEnd).format("YYYY-MM-DD") : null,
        customerCode: customerCode,
      }),
    onSuccess: (data) => {
      if (data.data.data) {
        setData(data.data.data);
        setFilteredData(data.data.data);
      } else {
        setData([])
        setFilteredData([])
      }
      refetch2();
    },
  });
  const { isFetching: isFetching2, refetch: refetch2 } = useQuery({
    queryKey: ["misc-dashboard-status", { customerCode }],
    enabled: enabled,
    queryFn: () =>
      getRecvDashboardStatusCount({
        orderType: "Misc",
        dateStart: Boolean(dateStart) ? dayjs(dateStart).format("YYYY-MM-DD") : null,
        dateEnd: Boolean(dateEnd) ? dayjs(dateEnd).format("YYYY-MM-DD") : null,
        customerCode: customerCode,
      }),
    onSuccess: (data) => {
      if (data.data.data)
        setTabs(data.data.data);
      else setTabs([
        { label: "Open", count: 0 },
        { label: "GRN", count: 0 },
        { label: "PutawayGenerated", count: 0 },
        { label: "Completed", count: 0 },
        { label: "Cancelled", count: 0 },
      ])
    },
  });

  const customProps = {
    startDate: dateStart,
    setStartDate: setDateStart,
    endDate: dateEnd,
    setEndDate: setDateEnd,
  };
  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const handleClick = (type, column) => {
    setDashboardType(type);
    setColumns(column);
  };

  return (
    <ListLayout>
      {(isFetching || isFetching2) && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={2.4}>
            <DefaultStatisticsCard
              title="Open"
              count={tabs.find((x) => x.label === "Open")?.count ?? 0}
              type={"Open"}
              column={miscOpenColumns}
              selected={dashboardType === "Open"}
              color={"#d9d9ff"}
              handleClick={handleClick}
            />
          </Grid>
          <Grid item xs={2.4}>
            <DefaultStatisticsCard
              title="Goods Received Note"
              count={tabs.find((x) => x.label === "GRN")?.count ?? 0}
              type={"GRN"}
              column={miscGRNColumns}
              selected={dashboardType === "GRN"}
              color={"#d9d9ff"}
              handleClick={handleClick}
            />
          </Grid>
          <Grid item xs={2.4}>
            <DefaultStatisticsCard
              title="Putaway Generated"
              count={tabs.find((x) => x.label === "PutawayGenerated")?.count ?? 0}
              type={"PutawayGenerated"}
              column={miscPutawayGeneratedColumns}
              selected={dashboardType === "PutawayGenerated"}
              handleClick={handleClick}
              color={"lightyellow"}
            />
          </Grid>
          <Grid item xs={2.4}>
            <DefaultStatisticsCard
              title="Completed"
              count={tabs.find((x) => x.label === "Completed")?.count ?? 0}
              type={"Completed"}
              column={miscCompletedColumns}
              selected={dashboardType === "Completed"}
              handleClick={handleClick}
              color={"#bff8d9"}
            />
          </Grid>
          <Grid item xs={2.4}>
            <DefaultStatisticsCard
              title="Cancelled"
              count={tabs.find((x) => x.label === "Cancelled")?.count ?? 0}
              type={"Cancelled"}
              column={miscCancelledColumns}
              selected={dashboardType === "Cancelled"}
              handleClick={handleClick}
              color={"lightpink"}
            />
          </Grid>
        </Grid>
      </MDBox>

      {/*dashboardType in full, PendingMisc_custCode_currentDateTime */}
      {/*completed and cancelled also use currentDateTime, not startDate_endDate */}
      <TableCard>
        <Grid container justifyContent="space-between" alignItems="flex-start" margin="10px">
          <Grid item xs={2}>
            {/* <MDButton color="info">
              <CSVLink
                style={{ color: "white" }}
                filename={
                  `${dashboardType}_Misc_${customerCode}_` + dayjs(new Date()).format("DD-MM-YYYY")
                }
                data={filteredData}
              >
                export
              </CSVLink>
            </MDButton> */}
            <MDButton
              color="info"
              onClick={() => {
                const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_Misc_${dashboardType}`;
                downloadCsv(filename, filteredData)
              }}
            >
              export
            </MDButton>
          </Grid>
          <Grid item xs={10}>
            <Grid justifyContent="flex-end" container spacing={3} sx={{ width: "100%" }}>
              <Grid item xs={6}>
                <CustomDateRangePicker {...customProps} />
              </Grid>
              <Grid item>
                <MDButton
                  color="info"
                  disabled={!dateStart || !dateEnd}
                  onClick={() => {
                    refetch();
                  }}
                >
                  search
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <BasicTable columns={columns} data={data} onFilterRowsChange={handleFilterRowsChange} />
      </TableCard>
    </ListLayout>
  );
}

export default MiscDashboard;
